/**
 * The doctrine's V4 cycle read, for screens (operator 2026-10-25: "make sure V4
 * is fully plumbed into all the consumers and no read is coming from other
 * older versions").
 *
 * The backend (engines/bocapital/v4/screen.py) attaches flat `v4_*` fields to
 * the cycle blocks it serves and a payload-level `doctrine_v4: {PAIR: fields}`
 * to /setups/scan and /setups/live. These helpers are the one place the UI
 * reads them.
 *
 * Rules:
 *  - `v4_available === false`  -> show "V4 no read — <reason>", never a legacy value.
 *  - `v4_available === undefined` (fields absent: CYCLE_VIEW_SOURCE=legacy, or a
 *    payload that never carried them) -> the caller may fall back to its old field.
 */

export type V4Direction = 'BULLISH' | 'BEARISH';
export type V4ResetState = 'NONE' | 'CONFIRMED' | 'FAILED' | 'PENDING';

export interface V4Fields {
  v4_available?: boolean;
  v4_unavailable_reason?: string | null;
  v4_level?: number | null;
  v4_direction?: V4Direction | null;
  v4_direction_word?: 'up' | 'down' | null;
  v4_phase?: 'UP' | 'DOWN' | null;
  v4_cycle_day?: number | null;
  v4_reset_state?: V4ResetState | string | null;
  v4_travelled_adr?: number | null;
  v4_cycle_complete?: boolean | null;
  v4_anchor_cross_utc?: string | null;
  v4_anchor_price?: number | null;
  v4_decision_bar_utc?: string | null;
  v4_summary?: string | null;
  v4_source?: 'doctrine' | string;
  /** true: the newest read the candle store has is older than the last closed bar (summary shows its bar) */
  v4_lagging?: boolean;
}

export const V4_FIELD_KEYS: readonly (keyof V4Fields)[] = [
  'v4_available', 'v4_unavailable_reason', 'v4_level', 'v4_direction',
  'v4_direction_word', 'v4_phase', 'v4_cycle_day', 'v4_reset_state',
  'v4_travelled_adr', 'v4_cycle_complete', 'v4_anchor_cross_utc',
  'v4_anchor_price', 'v4_decision_bar_utc', 'v4_summary', 'v4_source', 'v4_lagging',
];

/** True when the object carries the doctrine V4 fields at all. */
export function hasV4<T extends V4Fields>(o: T | null | undefined): o is T & { v4_available: boolean } {
  return !!o && typeof o.v4_available === 'boolean';
}

/** The reason V4 has no read, or null when it has one (or carries no V4 fields). */
export function v4NoReadReason(o: V4Fields | null | undefined): string | null {
  if (!hasV4(o) || o.v4_available) return null;
  return o.v4_unavailable_reason || 'unknown';
}

/** "V4 no read — NO_M15_DATA" */
export function v4NoReadText(o: V4Fields | null | undefined): string {
  return `V4 no read — ${v4NoReadReason(o) ?? 'unknown'}`;
}

/** V4's reset state when it is not NONE (e.g. "CONFIRMED"), else null. */
export function v4ActiveReset(o: V4Fields | null | undefined): string | null {
  if (!hasV4(o) || !o.v4_available) return null;
  const r = String(o.v4_reset_state || '').toUpperCase();
  return r && r !== 'NONE' ? r : null;
}

/** "L2" from V4, "V4 n/a" when V4 has no read, null when the fields are absent. */
export function v4LevelText(o: V4Fields | null | undefined): string | null {
  if (!hasV4(o)) return null;
  if (!o.v4_available) return 'V4 n/a';
  return o.v4_level != null ? `L${o.v4_level}` : 'V4 n/a';
}

/** Short V4 read, e.g. "V4 L2 down" / "V4 no read"; null when the fields are absent. */
export function v4ShortText(o: V4Fields | null | undefined): string | null {
  if (!hasV4(o)) return null;
  if (!o.v4_available) return 'V4 no read';
  const lvl = o.v4_level != null ? ` L${o.v4_level}` : '';
  const dir = o.v4_direction_word ? ` ${o.v4_direction_word}` : '';
  return `V4${lvl}${dir}`;
}

/**
 * The pair's CURRENT doctrine V4 beside a setup, labelled "V4 now" so an older
 * setup's own level is never misread as today's (review 2026-10-25):
 * "V4 now L2 down" / "V4 now: no read — NO_M15_DATA"; null when absent.
 */
export function v4NowText(o: V4Fields | null | undefined): string | null {
  if (!hasV4(o)) return null;
  if (!o.v4_available) return `V4 now: no read — ${v4NoReadReason(o) ?? 'unknown'}`;
  const lvl = o.v4_level != null ? ` L${o.v4_level}` : '';
  const dir = o.v4_direction_word ? ` ${o.v4_direction_word}` : '';
  return `V4 now${lvl}${dir}`;
}

function pairKey(pair: unknown): string {
  return String(pair || '').replace('=X', '').replace('/', '').replace('_', '').toUpperCase();
}

/** The doctrine V4 for a setup: attached by the API client from the payload's
 * `doctrine_v4` map (see api.ts scanSetups / getLiveSetups). */
export function setupDoctrineV4(s: { doctrine_v4?: V4Fields; [k: string]: any } | null | undefined): V4Fields | undefined {
  return s && hasV4(s.doctrine_v4) ? s.doctrine_v4 : undefined;
}

/** Attach `doctrine_v4[pair]` to each setup as `s.doctrine_v4` (client side only;
 * the backend setup dicts are untouched). Setups without an entry are returned as is. */
export function attachDoctrineV4<T extends { [k: string]: any }>(
  setups: T[] | null | undefined,
  map: Record<string, V4Fields> | null | undefined,
): T[] {
  const list = Array.isArray(setups) ? setups : [];
  if (!map || typeof map !== 'object') return list;
  return list.map((s) => {
    const f = map[pairKey(s?.pair ?? s?.symbol)];
    return f ? { ...s, doctrine_v4: f } : s;
  });
}

/**
 * Safety net for the websocket `cycle:state` push. The backend sends the V4
 * display copy on that push too (api/realtime_v3.emit_cycle_state ->
 * v4/screen.display_state), so a normal push already carries the v4_* fields
 * and passes through unchanged. Only a push that arrives WITHOUT them (a
 * legacy-shaped push, or the server's V4 overlay failed) keeps the V4 read of
 * the last REST poll (it changes once per closed M15 bar) instead of putting
 * the state engine's level/phase/reset back on screen.
 */
export function keepV4Cycle<C extends { [k: string]: any }>(prev: C | undefined, next: C | undefined): C | undefined {
  if (!next || hasV4(next as V4Fields) || !prev || !hasV4(prev as V4Fields)) return next;
  const out: { [k: string]: any } = { ...next };
  for (const k of V4_FIELD_KEYS) out[k] = (prev as any)[k];
  for (const k of ['level', 'direction', 'move_multiple_adr', 'cycle_day', 'phase', 'cycle_source']) {
    out[k] = (prev as any)[k];
  }
  for (const k of ['cycle_phase', 'is_reset', 'reset_confidence']) delete out[k];
  return out as C;
}
